import { create } from 'zustand';
import { useMemo } from 'react';
import { OrderFormData } from '@/feature/order_entry/order_schema.ts';
import { immer } from 'zustand/middleware/immer';
import { devtools } from 'zustand/middleware';
import {
  Order,
  OrderFailureReason,
  OrderID,
  OrderStatusCanceled,
  OrderStatusReason,
  OrderStatusReasonUnspecified,
  OrderStatusReasonOrderCancelled,
  OrderStatusReasonOrderChanged,
  RequestID,
} from '@/api/spec.ts';
import { handleOrderStatusUpdate, handlePostingStatusChange, isChangeOrderUiKey } from './utils/orderNotifications.ts';
import { getOrderErrorInfo } from './utils/orderErrorHandling.ts';
import { getOrderStatusFacts } from './utils/orderStatusFacts.ts';
import { MarketData } from '@/common/MarketData.ts';
import { ORDER_BACKSTOP_TIMEOUT_MS } from '@/common/timeout_constants.ts';

import { BigIntDevtoolsSerialize } from '@/utils/zustand_helpers.ts';

export type OrderPostingStatus = 'idle' | 'preparing' | 'confirming' | 'confirmed' | 'failed';

/** Statuses in which the posting still reports its own outcome — see `postingWillExplain`. */
const inFlightPostingStatuses: OrderPostingStatus[] = ['preparing', 'confirming'];

/**
 * How many *settled* `rq → uiKey` pairs to remember. The exchange keeps emitting updates after a
 * posting settles, each retry attempt under a fresh `oid`; without this map they lose the identity
 * of the user action and each opens its own toast. Entries still in flight don't count against the
 * cap — a burst larger than it used to evict exactly the ones still needed.
 */
const MAX_TRACKED_REQUEST_UI_KEYS = 50;

/** Same bound and reason as above, for attributing a `StatusResponse` to the request it rejected. */
const MAX_TRACKED_FRAME_SEQ_NOS = 50;

export interface OrderPostingState {
  uiKey?: string; // Stable ID for the user action (persists across retries)
  requestId?: RequestID; // Unique ID for the specific attempt (rotates on retry)
  orderData?: OrderFormData;
  submittedBlockNumber?: number;
  status: OrderPostingStatus;
  error?: OrderStatusReason;
  /** Refines `error` when the exchange published a cause (`Order.fr`). */
  failureReason?: OrderFailureReason;
  errorMessage?: string;
  retryCount: number;
}

/**
 * Everything needed to watch one sent request, in ONE record — the window and the backstop used to
 * be separate registries (a map in `ui-kit`'s heartbeat store, a `setTimeout` map in
 * `usePostOrderAPI`), so "is request R still being watched?" was written in three places that could
 * disagree. Now **removal from `inFlightRequests` is the only way to stop watching**.
 *
 * `posting` stays a single slot — it drives the toast and the form status — but a burst (TP/SL,
 * cancelAll, closeAll) puts several requests in flight at once, each needing its own report.
 */
export interface InFlightRequest {
  uiKey: string;
  orderData?: OrderFormData;
  submittedBlockNumber?: number;
  retryCount: number;
  /**
   * Sequence number from which silence is conclusive; `null` while unarmed — no heartbeat to
   * measure against, or this attempt's window is spent and a retry owns it. Only the backstop can
   * judge a request in that state.
   */
  deadlineBlock: number | null;
  /** False once the exchange acknowledged the request: it landed, so re-sending could duplicate it. */
  canRetry: boolean;
  /** Wall-clock ms after which the request is reported regardless. Data, so no timer can leak. */
  backstopAt: number;
  /** Names the request in its own failure toast, for requests the user did not aim at directly. */
  label?: string;
  /**
   * A TP/SL leg. The exchange reports such an order's later lifecycle under the PARENT's `rq` (the
   * one named in `tr`), so the leg's own id is answered once, by the acknowledgement, and never
   * again — settling on it is correct rather than lenient.
   */
  secondary?: boolean;
  /** The legs sent with this request, so a retry can cancel what it supersedes. */
  legRequestIds?: RequestID[];
}

interface OpenOrdersStore {
  orders: Map<OrderID, Order>;
  posting: OrderPostingState;
  /** Every request sent and not yet settled — see `InFlightRequest`. */
  inFlightRequests: Map<RequestID, InFlightRequest>;
  /** Bounded `rq → uiKey`; outlives the posting slot so late updates keep their action identity. */
  requestUiKeys: Map<RequestID, string>;
  /** Bounded `frame sn → rq`. `StatusResponse.cid` echoes the frame's `sn`; nothing else links them. */
  requestByFrameSeqNo: Map<number, RequestID>;

  // Single order operations
  initiatePosting: (params: {
    uiKey: string;
    requestId: RequestID;
    submittedBlockNumber?: number;
    orderData?: OrderFormData;
    /** Set only by the two automatic retry paths — see the retry-budget comment in the body. */
    isRetry?: boolean;
  }) => void;
  setConfirming: () => void;
  setConfirmed: () => void;
  setFailed: (
    requestId: RequestID | undefined,
    error: OrderStatusReason,
    errorMessage?: string,
    failureReason?: OrderFailureReason
  ) => void;
  resetPosting: () => void;
  /** Arm (or re-arm) the block deadline. A no-op once the request settled — removal is disarming. */
  armRequest: (requestId: RequestID, deadlineBlock: number | null, canRetry: boolean) => void;
  /**
   * Drop the deadline without settling: this attempt's window is spent, the retry arms its own.
   * `canRetry` is left alone — it is a fact about the exchange's answer, not about which attempt.
   */
  disarmRequest: (requestId: RequestID) => void;
  /**
   * Watch a request that must NOT take the posting slot — the TP/SL legs sent with a parent order.
   * Untracked, a leg the exchange rejected vanished while the parent reported success.
   */
  trackSecondaryRequest: (params: {
    requestId: RequestID;
    uiKey: string;
    label: string;
    /** Records the leg on its parent, so a retry can clean up what it supersedes. */
    parentRequestId?: RequestID;
    submittedBlockNumber?: number;
  }) => void;
  /** Call with whatever `sendMessage` returned for the frame carrying `requestId`. */
  trackSentFrame: (frameSeqNo: number, requestId: RequestID) => void;
  /**
   * Report a request that never resolved. Settles the posting when it owns `requestId`; otherwise
   * still fires that request's own failure toast — the only way a burst's other requests are heard.
   */
  failInFlightRequest: (requestId: RequestID, error: OrderStatusReason, errorMessage: string) => void;
  clearInFlightRequests: () => void;

  // Order management
  setOrdersSnapshot: (orders: Order[]) => void;
  patchOrder: (order: Order, markets?: ReadonlyMap<number, MarketData>) => void;
  reset: () => void;
}

const initialState: Pick<OpenOrdersStore, 'posting' | 'orders' | 'inFlightRequests' | 'requestUiKeys' | 'requestByFrameSeqNo'> =
  {
    posting: {
      status: 'idle',
      retryCount: 0,
    },
    orders: new Map(),
    inFlightRequests: new Map(),
    requestUiKeys: new Map(),
    requestByFrameSeqNo: new Map(),
  };

/** Map keeps insertion order — evict oldest first, but never an entry still in flight. */
const evictOldest = <K, V>(map: Map<K, V>, max: number, isLive: (key: K, value: V) => boolean) => {
  if (map.size <= max) return;
  for (const [key, value] of map) {
    if (map.size <= max) return;
    if (isLive(key, value)) continue;
    map.delete(key);
  }
};

/**
 * A settled request stops being watched. Central so every settling path retires it the same way —
 * one left behind keeps the form disabled until its wall-clock backstop fires.
 */
const retireRequest = (state: OpenOrdersStore, requestId: RequestID | undefined) => {
  if (requestId == null) return;
  state.inFlightRequests.delete(requestId);
};

/**
 * Report a failure the posting slot cannot speak for — a burst moved the slot on, or it belongs to
 * another flow. `previousStatus` only has to differ from `failed`; the `posting:{uiKey}` replaceKey
 * is what replaces that request's own "Confirming…" toast, which nothing else can dismiss.
 */
const reportOrphanedFailure = (
  state: OpenOrdersStore,
  requestId: RequestID,
  error: OrderStatusReason,
  errorMessage?: string,
  failureReason?: OrderFailureReason
) => {
  const request = state.inFlightRequests.get(requestId);
  retireRequest(state, requestId);
  // Already settled: the first status per `rq` wins and every later one is ignored, this included.
  if (!request) return;

  handlePostingStatusChange('failed', 'confirming', {
    orderData: request.orderData,
    errorReason: error,
    failureReason,
    errorMessage: request.label && errorMessage ? `${request.label}: ${errorMessage}` : errorMessage,
    requestId,
    uiKey: request.uiKey,
    retryCount: request.retryCount,
  });
};

const updatePostingToConfirmed = (state: OpenOrdersStore, oid?: number) => {
  const previousStatus = state.posting.status;
  state.posting.status = 'confirmed';
  retireRequest(state, state.posting.requestId);

  handlePostingStatusChange('confirmed', previousStatus, {
    orderData: state.posting.orderData,
    errorReason: state.posting.error,
    requestId: state.posting.requestId,
    uiKey: state.posting.uiKey,
    retryCount: state.posting.retryCount,
    oid,
  });
};

const updatePostingToFailed = (
  state: OpenOrdersStore,
  error: OrderStatusReason,
  errorMessage?: string,
  oid?: number,
  failureReason?: OrderFailureReason
) => {
  const previousStatus = state.posting.status;
  state.posting.status = 'failed';
  state.posting.error = error;
  state.posting.failureReason = failureReason;
  state.posting.errorMessage = errorMessage;
  retireRequest(state, state.posting.requestId);

  handlePostingStatusChange('failed', previousStatus, {
    orderData: state.posting.orderData,
    errorReason: error,
    failureReason,
    errorMessage,
    requestId: state.posting.requestId,
    uiKey: state.posting.uiKey,
    retryCount: state.posting.retryCount,
    oid,
  });
};

export const useOpenOrdersStore = create<OpenOrdersStore>()(
  devtools(
    immer((set, get) => ({
      ...initialState,
      initiatePosting: ({ uiKey, requestId, submittedBlockNumber, orderData, isRetry = false }) =>
        set((state) => {
          // Attempts at the same user action — and ONLY the retry paths may inherit the count.
          // `uiKey` is stable per order (`change:{oid}`) and a settled slot keeps it, so treating
          // every repeat as a retry made a fresh edit of an already-edited order start with its
          // budget spent. Sources in order: the superseded attempt (a retry replaces it, its
          // window having elapsed), else the slot — the `OrderDescIdTooLow` re-submit runs after
          // its failure retired the request, leaving the slot as the only record. The slot alone
          // is never enough: a burst moves it to another action between an attempt and its retry.
          let retryCount = 0;
          for (const [id, request] of state.inFlightRequests) {
            if (request.uiKey !== uiKey) continue;
            if (isRetry) retryCount = Math.max(retryCount, request.retryCount + 1);
            // A repeat supersedes the older attempt either way: same `uiKey`, same toast identity.
            state.inFlightRequests.delete(id);
          }
          if (isRetry && state.posting.uiKey === uiKey) {
            retryCount = Math.max(retryCount, state.posting.retryCount + 1);
          }

          // Unarmed until the frame is accepted (`armRequestWindow` after `sendMessage`), but the
          // backstop starts here — a request that sends and is never armed has nothing else on it.
          state.inFlightRequests.set(requestId, {
            uiKey,
            orderData,
            submittedBlockNumber,
            retryCount,
            deadlineBlock: null,
            canRetry: true,
            backstopAt: Date.now() + ORDER_BACKSTOP_TIMEOUT_MS,
          });

          // Outlives the posting slot so later updates can still name the action they came from.
          // Recorded AFTER the in-flight record exists — that is what the cap consults, and a
          // moment earlier every new entry looks settled and evicts itself on the spot.
          state.requestUiKeys.set(requestId, uiKey);
          evictOldest(state.requestUiKeys, MAX_TRACKED_REQUEST_UI_KEYS, (id) => state.inFlightRequests.has(id));

          state.posting.retryCount = retryCount;
          state.posting.uiKey = uiKey;

          state.posting.requestId = requestId;
          state.posting.submittedBlockNumber = submittedBlockNumber;
          state.posting.orderData = orderData;
          state.posting.status = 'preparing';
          state.posting.error = undefined;
          state.posting.failureReason = undefined;
          state.posting.errorMessage = undefined;
        }),
      setConfirming: () =>
        set((state) => {
          if (state.posting.status === 'preparing') {
            const previousStatus = state.posting.status;
            state.posting.status = 'confirming';

            handlePostingStatusChange('confirming', previousStatus, {
              orderData: state.posting.orderData,
              errorReason: state.posting.error,
              requestId: state.posting.requestId,
              uiKey: state.posting.uiKey,
              retryCount: state.posting.retryCount,
            });
          }
        }),
      setConfirmed: () =>
        set((state) => {
          updatePostingToConfirmed(state);
        }),
      setFailed: (requestId, error, errorMessage, failureReason) =>
        set((state) => {
          // No request id: the on-chain hook, whose error handler runs with `useNotifications:
          // false`, so this is the ONLY thing reporting its pre-submit failures. Nothing to dedup
          // against, so a slot left `failed` earlier is stale rather than a repeat — and taking
          // over a slot a live API request owns would retire that request behind its back.
          if (requestId == null) {
            const slotCanReport = state.posting.requestId == null && state.posting.status !== 'failed';
            if (slotCanReport) {
              updatePostingToFailed(state, error, errorMessage, undefined, failureReason);
              return;
            }
            // Standalone: no `uiKey`, so it collapses with nothing and replaces nothing.
            handlePostingStatusChange('failed', 'confirming', { errorReason: error, failureReason, errorMessage });
            return;
          }

          // Not what the slot holds — a stale attempt after a retry rotated the id, or one of a
          // burst's other requests. Retiring it silently left the "Confirming…" toast up forever.
          if (requestId !== state.posting.requestId) {
            reportOrphanedFailure(state, requestId, error, errorMessage, failureReason);
            return;
          }

          // Same request, already decided: the first status per `rq` wins, so this is a repeat.
          if (state.posting.status === 'confirmed' || state.posting.status === 'failed') {
            retireRequest(state, requestId);
            return;
          }

          updatePostingToFailed(state, error, errorMessage, undefined, failureReason);
        }),
      resetPosting: () =>
        set((state) => {
          state.posting = initialState.posting;
        }),

      trackSecondaryRequest: ({ requestId, uiKey, label, parentRequestId, submittedBlockNumber }) =>
        set((state) => {
          state.inFlightRequests.set(requestId, {
            uiKey,
            label,
            secondary: true,
            submittedBlockNumber,
            retryCount: 0,
            deadlineBlock: null,
            canRetry: true,
            backstopAt: Date.now() + ORDER_BACKSTOP_TIMEOUT_MS,
          });

          if (parentRequestId != null) {
            const parent = state.inFlightRequests.get(parentRequestId);
            if (parent) parent.legRequestIds = [...(parent.legRequestIds ?? []), requestId];
          }
          state.requestUiKeys.set(requestId, uiKey);
          evictOldest(state.requestUiKeys, MAX_TRACKED_REQUEST_UI_KEYS, (id) => state.inFlightRequests.has(id));
        }),

      armRequest: (requestId, deadlineBlock, canRetry) =>
        set((state) => {
          const request = state.inFlightRequests.get(requestId);
          if (!request) return;
          request.deadlineBlock = deadlineBlock;
          request.canRetry = canRetry;
        }),

      disarmRequest: (requestId) =>
        set((state) => {
          const request = state.inFlightRequests.get(requestId);
          if (!request) return;
          request.deadlineBlock = null;
        }),

      trackSentFrame: (frameSeqNo, requestId) =>
        set((state) => {
          state.requestByFrameSeqNo.set(frameSeqNo, requestId);
          evictOldest(state.requestByFrameSeqNo, MAX_TRACKED_FRAME_SEQ_NOS, (_sn, id) => state.inFlightRequests.has(id));
        }),

      failInFlightRequest: (requestId, error, errorMessage) =>
        set((state) => {
          if (state.posting.requestId === requestId && inFlightPostingStatuses.indexOf(state.posting.status) !== -1) {
            updatePostingToFailed(state, error, errorMessage);
            return;
          }

          reportOrphanedFailure(state, requestId, error, errorMessage);
        }),

      clearInFlightRequests: () =>
        set((state) => {
          state.inFlightRequests.clear();
        }),

      setOrdersSnapshot: (orders) =>
        set((state) => {
          const ordersMap = new Map<OrderID, Order>();

          orders.forEach((order) => {
            // Same gate as patchOrder: a snapshot must not seed a row that no live update evicts.
            if (!order.r && getOrderStatusFacts(order.st).live) {
              ordersMap.set(order.oid, order);
            }
          });

          state.orders = ordersMap;
        }),
      patchOrder: (order, markets) =>
        set((state) => {
          const previousOrder = order.oid != null ? state.orders.get(order.oid) : undefined;
          const previousStatus = previousOrder?.st;
          const isRemoved = order.r;
          const facts = getOrderStatusFacts(order.st);

          if (order.oid == null) {
            console.warn('Received order without ID:', order);
            return;
          } else if (isRemoved || facts.terminal) {
            state.orders.delete(order.oid);
          } else if (facts.live) {
            state.orders.set(order.oid, order);
          }

          // Pass uiKey if order matches posting request - ensures posting:{uiKey} replaceKey for entire flow
          const isFromPosting = state.posting.requestId && state.posting.requestId === order.rq;
          const uiKey = isFromPosting ? state.posting.uiKey : undefined;
          // Survives the posting slot, so retry attempts and the exchange's per-attempt `oid`s all
          // key their toast the same way. Falls back to the slot for a request predating the map.
          const actionKey = (order.rq != null ? state.requestUiKeys.get(order.rq) : undefined) ?? uiKey;
          // `requestId` outlives the posting, so a resting order failing to settle later still
          // matches one that confirmed long ago — and the branch below ignores that update.
          const postingWillExplain = Boolean(isFromPosting) && inFlightPostingStatuses.indexOf(state.posting.status) !== -1;
          handleOrderStatusUpdate(order, { previousStatus, postingUiKey: uiKey, actionKey, markets, postingWillExplain });

          // Handle posting status for orders from our posting request
          if (isFromPosting) {
            if (facts.settles === 'failed') {
              // If we already confirmed or failed this request, ignore this subsequent failure.
              if (state.posting.status === 'confirmed' || state.posting.status === 'failed') {
                console.log(
                  `[Orders] Ignoring failed order update for request ${order.rq} (already defined as ${state.posting.status})`
                );
              } else {
                const errorInfo = getOrderErrorInfo(order.sr || OrderStatusReasonUnspecified, undefined, order.fr);
                updatePostingToFailed(state, order.sr, errorInfo.message, order.oid, order.fr);
              }
            } else if (facts.settles === 'confirmed') {
              // `notifies` means the order toast already carries this event, so the posting only
              // takes the slot to `confirmed`. Both sides read the same table on purpose: when they
              // disagreed, a partial fill produced no toast from either.
              if (facts.notifies && isChangeOrderUiKey(state.posting.uiKey)) {
                updatePostingToConfirmed(state, order.oid);
              } else if (facts.notifies) {
                state.posting.status = 'confirmed';
              } else {
                if (order.st === OrderStatusCanceled && state.posting.orderData) {
                  // "Cancelled because changed" (sr=31) is the OLD entity of a successful
                  // change request (cancel-and-replace) — the replacement order's Open
                  // update confirms the posting, so it must not be treated as a failure.
                  if (order.sr !== OrderStatusReasonOrderChanged) {
                    const reason = order.sr || OrderStatusReasonOrderCancelled;
                    const errorInfo = getOrderErrorInfo(reason, undefined, order.fr);
                    updatePostingToFailed(state, reason, errorInfo.message, order.oid, order.fr);
                  }
                } else {
                  updatePostingToConfirmed(state, undefined); // undefined keeps posting:{uiKey} format
                }
              }
            }
          }

          // Retires any request this update decides, not just the posting's own. `trackRequestUpdate`
          // reads the result to decide whether to drop that request's timeout window, so the two
          // stay in step by construction.
          const trackedRequest = order.rq ? state.inFlightRequests.get(order.rq) : undefined;
          if (order.rq && trackedRequest) {
            // A leg's own `rq` appears exactly ONCE, on the `Pending` acknowledgement; everything
            // after — `Untriggered` included — is attributed to the parent named in `tr` (devnet:
            // leg `rq: 22 → st: 1`, then the same `oid` as `st: 8, sr: 31` under parent `rq: 21`).
            // So the acknowledgement IS the outcome; waiting for more waits forever.
            if (trackedRequest.secondary) {
              retireRequest(state, order.rq);
            } else {
              // The old entity of a cancel-and-replace is not an outcome — the replacement's
              // `Open`, under the same `rq`, is. Retiring here disarms a request still awaiting news.
              const isSupersededByChange = order.st === OrderStatusCanceled && order.sr === OrderStatusReasonOrderChanged;

              if (!isSupersededByChange && (facts.settles != null || isRemoved)) {
                retireRequest(state, order.rq);
              }
            }
          }
        }),
      reset: () =>
        set((state) => {
          state.orders = new Map();
          state.posting = initialState.posting;
          state.inFlightRequests = new Map();
          state.requestUiKeys = new Map();
          state.requestByFrameSeqNo = new Map();
        }),
    })),
    {
      name: 'dex-orders',
      ...BigIntDevtoolsSerialize,
    }
  )
);

export const useAllOrders = (): Order[] => {
  const orders = useOpenOrdersStore((state) => state.orders);

  return useMemo(() => {
    return Array.from(orders.values());
  }, [orders]);
};
